import Cookies from 'js-cookie';
import {jwtDecode} from 'jwt-decode';

import {AUTH_TOKEN, COMPLETED_STEP, REFRESH_TOKEN} from "../constants/Constants";
import {AppointmentSlotBookingStatus} from "../constants/AppointmentStatus";
import React from "react";
import {CaseManagementDocumentTypes} from "../constants/CaseManagementDocumentTypes";
import {UserTypes} from "../constants/UserTypes";
import {StreamDocument} from "../interfaces/StreamDocuments";
import {logoutUser} from "../api/AuthApi";


// Number of RCIC onboarding steps, and must stay in sync with STATUS_KEYS in
// RCICOnboardingSteps.tsx (licenseVerificationCompleted, serviceOfferedSetupCompleted).
// The security PIN is no longer an onboarding step — it is set on first access to
// Case Management or Wallet (see SideNavBar). RCICProfilePage hides the onboarding flow once
// numberOfCompletedSteps >= totalSteps, so a value lower than STATUS_KEYS.length
// marks an RCIC onboarded before they have actually finished.
export const totalSteps = 2;

/**
 * Returns the dashboard/profile path for the current user type.
 * Used for redirecting on access denied scenarios.
 */
export const getDashboardPath = (): string => {
    const userType = getUserType();
    if (userType === UserTypes.RCIC) return "/rcic/profile";
    if (userType === UserTypes.CLIENT) return "/client/profile";
    if (userType === UserTypes.ADMIN) return "/admin-dashboard";
    return "/";
};

/**
 * Secure cookie options — applied to all sensitive cookies.
 * - secure: only sent over HTTPS (falls back for localhost dev)
 * - sameSite: 'Strict' prevents cross-site request inclusion (CSRF protection)
 * NOTE: `expires` is intentionally omitted here; auth/refresh cookies set expiry
 * from the JWT's own `exp` claim (BUG-013 fix). Metadata cookies use a 24 h default.
 */
const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
const SECURE_COOKIE_OPTIONS: Cookies.CookieAttributes = {
    path: "/",
    secure: !isLocalhost,
    sameSite: 'Strict' as const,
};

/** 24-hour fallback expiry for metadata cookies (userType, firebase-token, completedStep). */
const METADATA_COOKIE_OPTIONS: Cookies.CookieAttributes = {
    ...SECURE_COOKIE_OPTIONS,
    expires: new Date(Date.now() + 24 * 60 * 60 * 1000),
};

/**
 * BUG-013 fix: derive cookie expiry from the JWT's own `exp` claim so the browser
 * cookie lifetime matches the token lifetime exactly.
 * Falls back to 8 hours if the token cannot be decoded.
 */
const getTokenExpiry = (token: string): Date => {
    try {
        const decoded: any = jwtDecode(token);
        if (decoded?.exp) return new Date(decoded.exp * 1000);
    } catch {}
    return new Date(Date.now() + 8 * 60 * 60 * 1000);
};

export function isStrong(password: string): boolean {
    // Calculate password strength
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSymbols = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const length = password.length;

    const criteriaMet1 = [hasUpperCase, hasLowerCase].filter(Boolean).length
    const criteriaMet2 = [hasNumbers, hasSymbols].filter(Boolean).length;

    return (length >= 8 && criteriaMet1 === 2 && criteriaMet2 >= 1);
}

export const setAuthTokenCookie = (cookie: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        try {
            Cookies.remove(AUTH_TOKEN);
            // BUG-013 fix: use JWT-derived expiry so the cookie lifetime matches the token.
            Cookies.set(AUTH_TOKEN, cookie, {...SECURE_COOKIE_OPTIONS, expires: getTokenExpiry(cookie)});
            resolve();
        } catch (error) {
            reject(error);
        }
    });
};
export const setUserType = (cookie: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        try {
            Cookies.remove("userType");
            Cookies.set("userType", cookie, {...METADATA_COOKIE_OPTIONS});
            resolve();
        } catch (error) {
            reject(error);
        }
    });
};
export const getUserType = (): string => {
    return Cookies.get("userType") as string;
};

/**
 * Checks whether the current auth token is present and not expired.
 * Returns true only if a valid, non-expired JWT exists.
 */
export const isTokenValid = (): boolean => {
    const token = Cookies.get(AUTH_TOKEN);
    if (!token || token === 'undefined') return false;
    try {
        const decoded: any = jwtDecode(token);
        if (!decoded.exp) return false;
        return new Date(decoded.exp * 1000) >= new Date();
    } catch {
        return false;
    }
};

/**
 * Extracts the user type from the JWT token payload.
 * Keycloak tokens typically store roles in realm_access.roles.
 * Falls back to the userType cookie for backward compatibility.
 */
export const getAuthenticatedUserType = (): string | null => {
    const token = Cookies.get(AUTH_TOKEN);
    if (token && token !== 'undefined') {
        try {
            const decoded: any = jwtDecode(token);
            // Check for a custom 'userType' claim in the JWT payload
            if (decoded.userType) return decoded.userType;
            // Check Keycloak realm_access.roles for known user types
            const roles: string[] = decoded?.realm_access?.roles ?? [];
            if (roles.includes('ADMIN')) return 'ADMIN';
            if (roles.includes('RCIC')) return 'RCIC';
            if (roles.includes('CLIENT')) return 'CLIENT';
            if (roles.includes('SUBSCRIBER')) return 'CLIENT'; // legacy role name
        } catch {
            // JWT decode failed — fall through to cookie
        }
    }
    // Fallback: read from cookie (backward compatibility)
    return getUserType() || null;
};

/**
 * Get the user's registration provider (auth method) from the JWT token.
 * 
 * @returns 'GOOGLE' or 'LOCAL', defaults to 'LOCAL' for backward compatibility
 */
export const getRegistrationProvider = (): 'GOOGLE' | 'LOCAL' => {
    // Try to get from localStorage first (set during login response)
    const storedProvider = localStorage.getItem('REGISTRATION_PROVIDER');
    if (storedProvider === 'GOOGLE' || storedProvider === 'LOCAL') {
        return storedProvider;
    }
    
    // Fallback to JWT claim for backward compatibility
    const token = Cookies.get(AUTH_TOKEN);
    if (token && token !== 'undefined') {
        try {
            const decoded: any = jwtDecode(token);
            if (decoded.registrationProvider) {
                return decoded.registrationProvider === 'GOOGLE' ? 'GOOGLE' : 'LOCAL';
            }
        } catch {
            // JWT decode failed — fall through to default
        }
    }
    // Default to LOCAL for backward compatibility
    return 'LOCAL';
};

/** Key an earlier build used to mirror the refresh token into sessionStorage. */
const LEGACY_REFRESH_TOKEN_KEY = 'blift-refresh-token';

export const setRefreshTokenCookie = (cookie: string): void => {
    Cookies.remove(REFRESH_TOKEN);
    // BUG-013 fix: use refresh-token's own JWT expiry (Keycloak refresh tokens are JWTs).
    Cookies.set(REFRESH_TOKEN, cookie, {...SECURE_COOKIE_OPTIONS, expires: getTokenExpiry(cookie)});
    // The refresh token is a credential, so it is kept only in the cookie — which is
    // Secure + SameSite=Strict and expires with the token itself. The old sessionStorage
    // mirror outlived the cookie and carried none of those protections; drop any copy
    // an earlier build left behind in this tab.
    sessionStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
};
export const getRefreshToken = (): string | null => {
    const fromCookie = Cookies.get(REFRESH_TOKEN);
    return fromCookie && fromCookie !== 'null' ? fromCookie : null;
};
export const setCompletedStep = (cookie: string): Promise<void> => {
    return new Promise((resolve, reject) => {
        try {
            Cookies.remove(COMPLETED_STEP);
            Cookies.set(COMPLETED_STEP, cookie, {...METADATA_COOKIE_OPTIONS});
            resolve();
        } catch (error) {
            reject(error);
        }
    });
};

export const getCompletedStep = (): string => {
    return Cookies.get(COMPLETED_STEP) as string;
};


export const setEmail = (email: string): void => {
    localStorage.setItem("email", email);
};



export const saveToLocal = (key: string, value: string): void => {
    localStorage.setItem(key, value);
};


export const getFromLocal = (key: string): string | null => {
    return localStorage.getItem(key);
};


export const logout = async (): Promise<void> => {
    try {
        const refreshToken = getRefreshToken();
        if (refreshToken && refreshToken !== 'null') {
            await logoutUser();
        }
    } catch (error) {
        console.error("Backend logout failed:", error);
    } finally {
        // Always clear local state regardless of backend logout result
        Cookies.remove(REFRESH_TOKEN);
        Cookies.remove("userType");
        Cookies.remove(AUTH_TOKEN);
        clearAllCookies();
        localStorage.clear();
        sessionStorage.clear(); // also clears blift-session-expiry
    }
};


/**
 * BUG-012 fix: delegates to isTokenValid() so that cookie presence AND JWT expiry
 * are both checked. Previously only checked cookie presence, returning true for
 * expired tokens still held in a live cookie.
 */
export const isLoggedIn = (): boolean => {
    return isTokenValid();
};

export const clearAllCookies = () => {
    const allCookies = Cookies.get();
    for (let cookie in allCookies) {
        Cookies.remove(cookie);
    }
};

export const transformStatusCode = (value: string) => {
    if (value in AppointmentSlotBookingStatus) {
        return transformStatusMessage(value as keyof typeof AppointmentSlotBookingStatus);
    } else {
        return value
            .toLowerCase()
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
}
export const formatDateForAPI = (date: Date | null): string => {
    return formatDateToInstant(date);
};


export const formatDateToInstant = (date: Date | null): string => {
    if (!date) return "";
    const pad = (n: number, z = 2) => String(n).padStart(z, '0');
    const tzOffsetMin = -date.getTimezoneOffset();
    const sign = tzOffsetMin >= 0 ? '+' : '-';
    const absOffset = Math.abs(tzOffsetMin);
    const offsetHours = Math.floor(absOffset / 60);
    const offsetMinutes = absOffset % 60;
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const ms = date.getMilliseconds();
    const msStr = ms ? '.' + String(ms).padStart(3, '0') : '';
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${msStr}${sign}${pad(offsetHours)}:${pad(offsetMinutes)}`;
};

function convertDateObj(date: Date | string): Date {
    if (typeof date === "string") {
        const localTimeString = formatToUserLocalTime(date);
        const parsedDate = new Date(localTimeString);
        if (isNaN(parsedDate.getTime())) {
            throw new Error("Invalid date string provided");
        }
        return parsedDate;
    }
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new Error("Invalid date object provided");
    }
    return date;
}

export const formatDate = (date: Date | string | null): string => {
    if (!date) return "";
    try {
        return convertDateObj(date).toLocaleDateString('en-NZ', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    } catch {
        return '';
    }
};

/** Format a date as Fri, 2026-05-19 */
export const formatDateYMD = (date: Date | string | null): string => {
    if (!date) return "";
    try {
        const d = convertDateObj(date);
        const dayName = d.toLocaleDateString('en-US', {weekday: 'short'});
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${dayName}, ${yyyy}-${mm}-${dd}`;
    } catch {
        return '';
    }
};

export const formatDateForStatusUpdate = (dateString: string | undefined) => {

    if (!dateString) return "";

    const date = new Date(dateString);
    const datePart = date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });
    const timePart = date.toLocaleString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    });
    return `${datePart} at ${timePart}`;
};

export const formatTime = (date: Date | string | null): string => {
    if (!date) return "";
    try {
        return convertDateObj(date).toLocaleTimeString('en-NZ', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch {
        return '';
    }
};

export function formatTimeRange(time: string): string {
    if (!time) return '';
    const [start, end] = time.split(' - ');
    return `${formatTime(new Date(start))} - ${formatTime(new Date(end))}`;
}

/**
 * BUG-020 fix: formats a date string in the user's local timezone using Intl.DateTimeFormat
 * instead of dayjs without timezone context. dayjs without the timezone plugin parses in the
 * browser's local time and can show the wrong date for users in non-local timezones.
 * Output example: "May 28, 2026 at 2:00 PM"
 */
export const formatDateMMDYYYYAthmA = (dateString: string | undefined): string => {
    if (!dateString) return "";
    let safe = dateString;
    if (!dateString.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateString)) safe += 'Z';
    const date = new Date(safe);
    if (isNaN(date.getTime())) return "";
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const datePart = date.toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true });
    return `${datePart} at ${timePart}`;
};

/**
 * BUG-020 fix: formats a date string as "YYYY-MM-DD, Weekday h:mm AM/PM" in the user's
 * local timezone. Previously used dayjs without timezone plugin — wrong date for non-local users.
 * Output example: "2026-05-22, Friday 2:00 PM"
 */
export const formatDateYYYYMMDDWithDay = (dateString: string | undefined): string => {
    if (!dateString) return "";
    let safe = dateString;
    if (!dateString.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateString)) safe += 'Z';
    const date = new Date(safe);
    if (isNaN(date.getTime())) return "";
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const year  = date.toLocaleDateString('en-US', { timeZone: tz, year: 'numeric' });
    const month = date.toLocaleDateString('en-US', { timeZone: tz, month: '2-digit' });
    const day   = date.toLocaleDateString('en-US', { timeZone: tz, day: '2-digit' });
    const weekday = date.toLocaleDateString('en-US', { timeZone: tz, weekday: 'long' });
    const timePart = date.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true });
    return `${year}-${month}-${day}, ${weekday} ${timePart}`;
};

export const formatStringToLocalDateTime = (dateString: string): string => {
    return formatToUserLocalTime(dateString);
};

/**
 * Formats a UTC date string to local date only — no day name, no time.
 * Example output: "March 7, 2026"
 */
export const formatStringToLocalDate = (dateString: string): string => {
    if (!dateString) return '-';
    let safeDateString = dateString;
    if (!dateString.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateString)) {
        safeDateString = dateString + 'Z';
    }
    const date = new Date(safeDateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

/**
 * Converts a UTC date string (ISO 8601) to a string formatted in the user's local time zone.
 *
 * @param dateString - The UTC date string from backend (e.g., "2025-07-28T17:00:00Z")
 * @returns The formatted date string in the user's local time zone
 */
export function formatToUserLocalTime(dateString: string) {
    if (!dateString) return '';

    // If no timezone, assume UTC by appending "Z"
    let safeDateString = dateString;
    if (!dateString.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateString)) {
        safeDateString = dateString + 'Z';
    }

    const date = new Date(safeDateString);
    if (isNaN(date.getTime())) return '';
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return date.toLocaleString('en-US', {
        timeZone: userTimeZone,
        dateStyle: 'full', timeStyle: 'short'
    });
}

export function formatToUserLocalTimeForRemarks(dateString: string) {
    if (!dateString) return '';

    // If no timezone, assume UTC by appending "Z"
    let safeDateString = dateString;
    if (!dateString.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateString)) {
        safeDateString = dateString + 'Z';
    }

    const date = new Date(safeDateString);
    if (isNaN(date.getTime())) return '';
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localDate = date.toLocaleDateString('en-NZ', {
        timeZone: userTimeZone,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });

    const localTime = date.toLocaleTimeString('en-US', {
        timeZone: userTimeZone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });

    return `${localDate} at ${localTime}`;
}


export const isPdf = (fileName: string) => {
    return fileName?.toLowerCase().endsWith('.pdf');
};

export const isImage = (fileName: string) => {
    return /\.(jpe?g|png|gif)$/i.test(fileName?.toLowerCase());
};

export const getImageExtension = (fileName: string) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    if (extension === 'jpg' || extension === 'jpeg') return 'jpeg';
    return extension;
};

export const capitalUnderScoreTextToNormalTextFormatter = (docType: string) => {
    return docType
        .toLowerCase() // convert to lowercase
        .split('_') // split by underscores
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // capitalize first letter of each word
        .join(' '); // join with spaces
};
export const setFirebaseToken = (cookie: string): void => {
    Cookies.remove("firebase-token");
    Cookies.set("firebase-token", cookie, {...METADATA_COOKIE_OPTIONS});
};
export const transformStatusMessage = (value: keyof typeof AppointmentSlotBookingStatus): string => {
    if (value === AppointmentSlotBookingStatus.INITIALIZED) {
        if (getUserType() === UserTypes.CLIENT)
            return "Consultation Request Sent"
        return "New Consultation Request";
    } else if (value === AppointmentSlotBookingStatus.DOCUMENT_SIGN_PENDING) {
        if (getUserType() === UserTypes.CLIENT)
            return "Consultation Agreement Pending";
        else if (getUserType() === UserTypes.RCIC)
            return "Consultation Agreement Sent - Pending Client Action";
        return "Document Sign Pending";
    } else if (value === AppointmentSlotBookingStatus.DOCUMENT_SIGNED) {
        return "Consultation Agreement Signed - Pending Payment";
    } else if (value === AppointmentSlotBookingStatus.APPOINTMENT_MEETING_LINK_SEND) {
        return "Meeting Scheduled";
    } else if (value === AppointmentSlotBookingStatus.PAYMENT_PENDING) {
        return "Pending Payment";
    } else if (value === AppointmentSlotBookingStatus.AWAITING_EXTERNAL_PAYMENT) {
        return "Awaiting External Payment";
    } else if (value === AppointmentSlotBookingStatus.PAYMENT_SUCCESSFUL) {
        if (getUserType() === UserTypes.CLIENT)
            return "Consultation Fee Paid – RCIC Scheduling Meeting";
        else if (getUserType() === UserTypes.RCIC)
            return "Consultation Fee Paid - Schedule Meeting";
        return "Payment Successful";
    } else if (value === AppointmentSlotBookingStatus.RETAINER_AGREEMENT_PENDING) {
        if (getUserType() === UserTypes.CLIENT)
            return "Service Agreement Received – Signature Required";
        else if (getUserType() === UserTypes.RCIC)
            return "Service Agreement Sent - Awaiting Client Signature";
        return "Service Agreement Pending";
    } else if (value === AppointmentSlotBookingStatus.RETAINER_AGREEMENT_SIGNED) {
        if (getUserType() === UserTypes.CLIENT)
            return "Service Agreement Signed - Case Setup Pending";
        else if (getUserType() === UserTypes.RCIC)
            return "Client Retained - Create Case";
        return "Service Agreement Signed";
    } else if (value === AppointmentSlotBookingStatus.REJECTED) {
        return "Declined Request";
    }else if (value === AppointmentSlotBookingStatus.ATTENDED) {
        return "Meeting Completed";
    }else if (value === AppointmentSlotBookingStatus.PAYMENT_FAILED) {
        return "Payment Failed";
    } else if (value === AppointmentSlotBookingStatus.PAID) {
        return "Payment Successful";
    }else if (value === AppointmentSlotBookingStatus.CLOSED) {
        return "Case Closed";
    }else if (value === AppointmentSlotBookingStatus.ACCEPT) {
        return "Booking Accepted";
    }else if (value === AppointmentSlotBookingStatus.PENDING) {
        return "Payment Pending";
    }else if (value === AppointmentSlotBookingStatus.RESCHEDULE_REQUESTED) {
        return "Reschedule Requested - Awaiting Response";
    }

    return value;
}

export function validatePostalOrZipCode(value: string, selectedCountry: number | null): string | undefined {
    if (selectedCountry === 2) {
        // Canadian postal code: A1B 2C3
        const regex = /^[A-Z]\d[A-Z] \d[A-Z]\d$/i;
        if (!regex.test(value.trim())) {
            return "Postal code must be in the format A1B 2C3";
        }
    } else {
        // Other: 5 digits
        const regex = /^\d{5}$/;
        if (!regex.test(value.trim())) {
            return "Zip code must be 5 digits";
        }
    }
    return undefined;
}

export function handlePostalCodeChange(
    e: React.ChangeEvent<HTMLInputElement>,
    selectedCountry: number | null,
    setFieldValue: (field: string, value: string) => void
) {
    let value = e.target.value;

    if (selectedCountry === 1) {
        // US zip code: digits only, max 5 characters
        value = value.replace(/\D/g, '').slice(0, 5);
    } else if (selectedCountry === 2) {
        // Canadian postal code A1B 2C3 — positions 0,2,4 = letter, positions 1,3,5 = digit
        // Strip everything except letters/digits, uppercase, cap at 6 clean chars
        const raw = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        let clean = '';
        for (let i = 0; i < raw.length && clean.length < 6; i++) {
            const ch = raw[i];
            const pos = clean.length;
            if (pos === 0 || pos === 2 || pos === 4) {
                if (/[A-Z]/.test(ch)) clean += ch;   // letter only
            } else {
                if (/\d/.test(ch)) clean += ch;       // digit only
            }
        }
        // Auto-insert space after the 3rd character
        value = clean.length > 3 ? clean.slice(0, 3) + ' ' + clean.slice(3) : clean;
    }

    setFieldValue("postalZipCode", value);
}

export function transformDocumentFileTypes(formName: CaseManagementDocumentTypes): string {
    switch (formName) {
        case 'IRCC_FORMS':
            return 'Required IRCC Forms';
        case 'ADDITIONAL_IRCC_FORMS':
            return 'Additional IRCC Forms';
        case 'SUPPORTING_DOCUMENTS':
            return 'Required Supporting Documents';
        case 'ADDITIONAL_SUPPORTING_DOCUMENTS':
            return 'Additional Supporting Documents';
        default:
            return capitalUnderScoreTextToNormalTextFormatter(formName);
    }
}

export const uniqueStreamDocuments = (oldArray: StreamDocument[], newArray: StreamDocument[]): StreamDocument[] => {
    const ids = new Set(oldArray.map(i => i?.document?.id));
    return [...newArray.filter(i => !ids.has(i.document?.id))];
};

export const stringNullCheck = (object: any) => {
    return (null != object && "" !== object && "null" !== object && undefined !== object)
}
/**
 * Formats a string by checking for null, undefined, or "null" values.
 * @param str
 */
export const formatString = (str: string): string => {
    if (stringNullCheck(str)) return str;
    else return '';
};

export const removeShareSuffix = (str: string): string => {
    if (!str) return '';
    return str
        .replace(/\s+(RCIC|BLift) share$/i, '')
        .replace(/\s+\(Status:.*?\)$/i, '')
        // Descriptions read "Initial Consultation Fee - RCIC share"; dropping the
        // share leaves a dangling separator behind.
        .replace(/\s*[-–—:]\s*$/, '')
        .trim();
};

/**
 * Joins the general part of an address — city, province/state and country — into a
 * single readable line, skipping the parts the profile left empty. The street lines
 * and postal code are deliberately left out. Returns an empty string when there is
 * nothing to show, so callers can fall back to their own placeholder.
 */
export const formatFullAddress = (address: any): string => {
    if (!address) return '';
    // Some endpoints send the address already flattened to a string.
    if (typeof address === 'string') return address.trim();
    return [
        address.city,
        address.province ?? address.state,
        address.country,
    ]
        .map((part) => (part == null ? '' : String(part).trim()))
        .filter((part) => part !== '' && part !== 'null')
        .join(', ');
};
